import {
  BulkUpdateResult,
  EnterpriseDepartmentUserType,
  Progress,
  ProgressBulkEditRequest,
  ProgressBulkSettingInterviewRequest,
  ProgressCheckResponse,
  ProgressCreateRequest,
  ProgressHistoryRemarksUpdateRequest,
  ProgressKpiResponse,
  ProgressOverwriteRequest,
  ProgressScreeningRequestStatus,
  ProgressScreeningRequestStatusHistory,
  ProgressSearchParams,
  ProgressSearchResponse,
  ProgressSettingSubScreeningAdjustRequest,
  ProgressStakeholderEmailResponse,
  ProgressSubScreeningStatus,
  ProgressSubScreeningStatusUpdateWithActionRequest,
  ProgressUpdateWithActionRequest,
  ProgressUpdateWithoutActionRequest,
} from '@agent-ds/shared/interfaces';
import { ApiService } from '@agent-ds/shared/models/api-service';
import { toAsialRequestParameters } from '@agent-ds/shared/util/util';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { REPORT_SUCCESS_HEADER } from '../auth.service';

@Injectable({
  providedIn: 'root',
})
export class ProgressApiService extends ApiService {
  constructor(private http: HttpClient) {
    super();
  }

  addProgress(request: ProgressCreateRequest): Observable<any> {
    return this.http.post<{ id: number }>('/api/progress', request, { headers: { [REPORT_SUCCESS_HEADER]: 'TRUE' } });
  }

  getList(params?: ProgressSearchParams): Observable<ProgressSearchResponse> {
    return this.http.get<ProgressSearchResponse>('/api/progress/search', {
      params: toAsialRequestParameters(params) as { [param: string]: string | string[] },
    });
  }

  updateUsers(progressId: number, users): Observable<{ id: number }> {
    return this.http.put<{ id: number }>(`/api/progress/${progressId}/users`, users);
  }

  updateHistoryRemarks(progressId: number, revision: number, remarks: ProgressHistoryRemarksUpdateRequest): Observable<{ id: number }> {
    return this.http.put<{ id: number }>(`/api/progress/${progressId}/history/${revision}`, remarks);
  }

  updateWithAction(progressId: number, requestObject: ProgressUpdateWithActionRequest): Observable<{ id: number }> {
    const markedRequestObject = this.markMachAction(requestObject);
    return this.http.put<{ id: number }>(`/api/progress/${progressId}`, markedRequestObject);
  }

  updateWithoutAction(progressId: number, requestObject: ProgressUpdateWithoutActionRequest): Observable<{ id: number }> {
    return this.http.put<{ id: number }>(`/api/progress/${progressId}/no-action`, requestObject);
  }

  delete(progressId: number): Observable<any> {
    return this.http.delete(`/api/progress/${progressId}`);
  }

  getDetail(progressId: number): Observable<Progress> {
    return this.http.get<Progress>(`/api/progress/${progressId}`);
  }

  revertHistory(progressId: number, revision: number, remarks: string): Observable<{ id: number }> {
    return this.http.put<{ id: number }>(`/api/progress/${progressId}/revert/${revision}`, null, { params: { remarks: remarks } });
  }

  bulkRegisterStudent(studentId: number, jobIds: number[]): Observable<any> {
    return this.http.put<BulkUpdateResult>(`/api/progress/bulk/student/${studentId}/register`, {
      jobIds: jobIds,
    });
  }

  bulkRegisterStudentsByJobId(jobId: number, studentIds: number[]): Observable<any> {
    return this.http.put<BulkUpdateResult>(`/api/progress/bulk/job/${jobId}/register`, {
      studentIds: studentIds,
    });
  }

  bulkRegisterStudentsByProgressIds(progressIds: number[]): Observable<any> {
    return this.http.put<BulkUpdateResult>('/api/progress/bulk/waiting-student-ok/overwrite', {
      progressIds: progressIds,
    });
  }

  bulkRecommendStudent(studentId: number, jobIds: number[]): Observable<any> {
    return this.http.put<BulkUpdateResult>(`/api/progress/bulk/student/${studentId}/recommend`, {
      jobIds: jobIds,
    });
  }

  bulkRegisterStudentNoUpdate(studentId: number, jobIds: number[]): Observable<any> {
    return this.http.put<BulkUpdateResult>(`/api/progress/bulk/student/${studentId}/register/no-update`, {
      jobIds: jobIds,
    });
  }

  bulkDecline(progressIds: number[]): Observable<BulkUpdateResult> {
    return this.http.put<BulkUpdateResult>('/api/progress/bulk/end', { progressIds: progressIds });
  }

  bulkEdit(progressIds: number[], request: ProgressBulkEditRequest): Observable<BulkUpdateResult> {
    return this.http.put<BulkUpdateResult>('/api/progress/bulk/edit', {
      progressIds: progressIds,
      fromStatus: request.fromStatus,
      toStatus: request.toStatus,
      stopReason: request.stopReason,
      remarks: request.remarks,
    });
  }

  bulkSettingInterview(request: ProgressBulkSettingInterviewRequest): Observable<BulkUpdateResult> {
    return this.http.put<BulkUpdateResult>('/api/progress/bulk/setting-interview', request);
  }

  bulkSettingSubScreening(request: ProgressSettingSubScreeningAdjustRequest): Observable<BulkUpdateResult> {
    return this.http.put<BulkUpdateResult>('/api/progress/sub-screening/bulk/setting-schedule', request);
  }

  // includeResumeCheck を渡さない既存の呼び出し元は、クエリにキーが付かずレスポンスも一切変わらない
  checkProgress(studentIds?: number[], jobIds?: number[], includeResumeCheck = false): Observable<ProgressCheckResponse[]> {
    const params: any = { studentIds: studentIds, jobIds: jobIds };
    if (includeResumeCheck) {
      params.includeResumeCheck = true;
    }
    return this.http.get<ProgressCheckResponse[]>('/api/progress/check', {
      params: toAsialRequestParameters(params) as { [param: string]: string | string[] },
    });
  }

  getStakeholders(progressIds: number[]): Observable<ProgressStakeholderEmailResponse[]> {
    return this.http.get<ProgressStakeholderEmailResponse[]>('/api/progress/stakeholders/email', {
      params: toAsialRequestParameters({ progressIds: progressIds }) as { [param: string]: string | string[] },
    });
  }

  overwrite(progressId: number, requestObject: ProgressOverwriteRequest): Observable<any> {
    return this.http.put(`/api/progress/${progressId}/overwrite`, requestObject);
  }

  getContractInfo(progressId: number): Observable<{ approvedContractIds: number[]; enterpriseBillingAddressIds: number[] }> {
    return this.http.get<{ approvedContractIds: number[]; enterpriseBillingAddressIds: number[] }>(
      `/api/progress/${progressId}/contract-info`,
    );
  }

  getReportRapa(date: { from: string; to: string }, teamId: number): Observable<ProgressKpiResponse> {
    return this.http.get<ProgressKpiResponse>('/api/progress/monthly-kpi/rapa', { params: { ...date, teamId: teamId + '' } });
  }

  getReportCa(date: { from: string; to: string }, teamId: number): Observable<ProgressKpiResponse> {
    return this.http.get<ProgressKpiResponse>('/api/progress/monthly-kpi/ca', { params: { ...date, teamId: teamId + '' } });
  }

  ngEnd(progressIds: number[]): Observable<BulkUpdateResult> {
    return this.http.put<BulkUpdateResult>('/api/progress/bulk/ng-end', { progressIds: progressIds });
  }

  bulkCreate(jobIds: number[], studentIds: number[]): Observable<BulkUpdateResult> {
    return this.http.post<BulkUpdateResult>('/api/progress/bulk', { jobIds: jobIds, studentIds: studentIds });
  }

  bulkCreateStudent(studentId: number, jobIds: number[], request: ProgressCreateRequest): Observable<any> {
    return this.http.post<BulkUpdateResult>(`/api/progress/bulk/student/${studentId}`, {
      ...request,
      jobIds: jobIds,
    });
  }

  bulkCreateJob(jobId: number, studentIds: number[], request: ProgressCreateRequest): Observable<any> {
    return this.http.post<BulkUpdateResult>(`/api/progress/bulk/job/${jobId}`, {
      ...request,
      studentIds: studentIds,
    });
  }

  bulkCreateCombination(combinations: { studentId: number; jobIds: number[] }[]): Observable<any> {
    return this.http.post<BulkUpdateResult>(`/api/progress/bulk/combination`, {
      studentIdJobIds: combinations,
    });
  }

  // 進捗対応事項ステータス一覧取得
  getProgressScreeningRequestStatusList(progressId: number): Observable<ProgressScreeningRequestStatus[]> {
    return this.http.get<ProgressScreeningRequestStatus[]>(`/api/progress/${progressId}/progress-screening-request-status-list`, {
      headers: { [REPORT_SUCCESS_HEADER]: "TRUE" }
    });
  }

  // 進捗対応事項ステータス履歴一覧取得
  getProgressScreeningRequestStatusHistoryList(progressId: number): Observable<ProgressScreeningRequestStatusHistory[]> {
    return this.http.get<ProgressScreeningRequestStatusHistory[]>(`/api/progress/${progressId}/progress-screening-request-status-history-list`, {
      headers: { [REPORT_SUCCESS_HEADER]: "TRUE" }
    });
  }

  // 進捗対応事項ステータス登録
  createProgressScreeningRequestStatus(
    progressId: number,
    progressScreeningRequestStatus: ProgressScreeningRequestStatus,
  ): Observable<any> {
    return this.http.post(`/api/progress/${progressId}/progress-screening-request-status`, progressScreeningRequestStatus, {
      headers: { [REPORT_SUCCESS_HEADER]: "TRUE" }
    });
  }

  // 進捗対応事項ステータス更新
  updateProgressScreeningRequestStatus(
    id: number,
    progressId: number,
    progressScreeningRequestStatus: ProgressScreeningRequestStatus,
  ): Observable<any> {
    return this.http.put(`/api/progress/${progressId}/progress-screening-request-status/${id}`, progressScreeningRequestStatus, {
      headers: { [REPORT_SUCCESS_HEADER]: "TRUE" }
    });
  }

  /**
   * メイン選考の対応事項タイプ名を同期
   */
  syncMainScreeningRequestType(progressId: number, requestType: string): Observable<any> {
    return this.http.put(`/api/progress/${progressId}/progress-screening-request-status/main/type`, {
      requestType
    });
  }

  // 進捗対応事項期限更新
  updateScreeningRequestDeadlines(progressId: number, seminarAt: string, jobScreeningInfoId?: number): Observable<any> {
    return this.http.put(`/api/progress/${progressId}/screening-requests/calculate-deadlines`, {
      seminarAt,
      jobScreeningInfoId
    });
  }

  // 進捗サブステータス対応事項期限更新
  updateSubScreeningRequestDeadlines(
    progressId: number,
    subStatusId: number,
    scheduledAt: string,
    jobScreeningInfoId?: number
  ): Observable<any> {
    return this.http.put(`/api/progress/${progressId}/sub-screening-requests/${subStatusId}/calculate-deadlines`, {
      scheduledAt,
      jobScreeningInfoId
    });
  }

  // 進捗対応事項ステータス削除
  deleteProgressScreeningRequestStatus(id: number, progressId: number): Observable<any> {
    return this.http.delete(`/api/progress/${progressId}/progress-screening-request-status/${id}`, {
      headers: { [REPORT_SUCCESS_HEADER]: "TRUE" }
    });
  }

  // 進捗サブ選考開始
  startProgressSubScreening(
    progressId: number,
    progressSubScreeningStatus: ProgressSubScreeningStatus,
  ): Observable<any> {
    return this.http.post(`/api/progress/${progressId}/sub-screening-start`, progressSubScreeningStatus, {
      headers: { [REPORT_SUCCESS_HEADER]: "TRUE" }
    });
  }
  // 進捗サブ選考更新
  updateProgressSubScreening(
    progressId: number,
    id: number,
    progressSubScreeningStatus: ProgressSubScreeningStatus,
  ): Observable<any> {
    return this.http.put(`/api/progress/${progressId}/sub-screening/${id}`, progressSubScreeningStatus, {
      headers: { [REPORT_SUCCESS_HEADER]: "TRUE" }
    });
  }

  updateProgressScreeningRequestStatusWithAction(
    progressId: number,
    id: number,
    requestObject: ProgressSubScreeningStatusUpdateWithActionRequest
  ): Observable<{ id: number }> {
    return this.http.put<{ id: number }>(
      `/api/progress/${progressId}/sub-screening/${id}/with-action`,
      requestObject,
    );
  }


  // 進捗サブ選考ステータス一覧取得
  getProgressSubScreeningList(
    progressId: number
  ): Observable<ProgressSubScreeningStatus[]> {
    return this.http.get<ProgressSubScreeningStatus[]>(`/api/progress/${progressId}/sub-screening-list`, {
      headers: { [REPORT_SUCCESS_HEADER]: "TRUE" }
    });
  }

  // 進捗サブ選考停止
  stopProgressSubScreening(progressId: number, id: number): Observable<any> {
    return this.http.delete(`/api/progress/${progressId}/sub-screening/${id}`, {
      headers: { [REPORT_SUCCESS_HEADER]: "TRUE" }
    });
  }

  bulkSeminarSchedule(
    progressIds: number[],
    jobSettingSeminarScheduleId: number
  ): Observable<BulkUpdateResult> {
    return this.http.put<BulkUpdateResult>(
      "/api/progress/bulk/seminar-schedule",
      { progressIds, jobSettingSeminarScheduleId }
    );
  }

  private markMachAction(request: ProgressUpdateWithActionRequest): ProgressUpdateWithActionRequest {
    const MACH_ACTIONS = [
      72, // セミナー日時決定
      74, // セミナー日時決定
      75, // 面接案内送信
      76, // 面接案内送信
      102, // N次面接日時決定
      105 // 面接案内送信
    ];

    return MACH_ACTIONS.includes(request.action)
      ? { ...request, isFromMach: true }
      : request;
  }
}
